import type { RedirectMechanismType, TrackingQueryStringKey, TravelCurrencyCode, TravelOfferProvider } from '@graphql/generated-base';
import type { AccommodationsSectionLayoutType } from '@shared/types/Accommodations.types';

export enum PartnerHotelIdType {
  hotels = 'hotels',
  joy = 'joy',
  stays = 'stays',
}

export enum PartnerSourceIdType {
  hotels = 'zh',
  joy = 'joyPlace',
  stays = 'nuitee',
}

export enum PartnerHotelDetailsRouteQueryParams {
  discountCode = 'discountCode',
  eventId = 'eventId',
  idType = 'idType',
  joyPlaceId = 'joyPlaceId',
  source = 'source',
  utm_campaign = 'utm_campaign',
  utm_content = 'utm_content',
  utm_medium = 'utm_medium',
  utm_source = 'utm_source',
  utm_source_secondary = 'utm_source_secondary',
}

export interface RedirectMechanismProps
  extends Readonly<{
    __typename?: 'RedirectMechanism';
    telemetryDestinationName: string;
    trackingQueryStringKey?: TrackingQueryStringKey | null;
    type: RedirectMechanismType;
    url: string;
    /**
     * The provider whose rate this redirect books. Non-null only when the redirect sends the guest
     * to book a specific rate. A non-null value can then be attributed to a displayed price, without
     * checking `type` first. Null on `partnerSearch`, which opens Joy's hotel search and books no
     * rate. Null on `custom`, which leaves the platform. Optional, because not every query selects it.
     */
    travelOfferProvider?: TravelOfferProvider | null;
  }> {}

export interface GoogleMapsProps
  extends Readonly<{
    lat?: number | null;
    lng?: number | null;
    gplid?: string | null;
    name?: string | null;
    address?: string | null;
    zentrumHubId?: string | null;
  }> {}

export interface Pricing
  extends Readonly<{
    digits: number;
    valueInMinorUnits: number;
    currency: TravelCurrencyCode;
    floatingPointDecimalString: string;
  }> {}

export interface PricingInfoProps
  extends Readonly<{
    nightlyBaseRate: Pricing;
    strikeoutNightlyBaseRate?: Pricing | null;
  }> {}

export interface TravelPlaceProps
  extends Readonly<{
    id?: string | null;
    url?: string | null;
    name?: string | null;
    customUrl?: string | null;
    type: 'joyPlace' | 'accommodationPlace';
  }> {}

export interface TelemetryHotelInfo
  extends Readonly<{
    hotelName?: string | null;
    hotelPrice?: string | null;
    hotelStrikeOutPrice?: string | null;
    hotelPriceCurrency?: TravelCurrencyCode | null;
  }> {}

export interface GuestPartnerHotelBookingRedirectParams
  extends TelemetryHotelInfo,
    Readonly<{
      telemetryName?: string;
      hotelSearchPageUrlType?: 'hotelSoldOut' | 'hotelWebsite'; // Currently support only hotelSoldOut interceptor, we will support different types.
      redirectMechanismType?: RedirectMechanismType;
      clientReference?: string;
      destinationName?: string;
      destinationUrl?: URL;
      zentrumHub?: {
        readonly id: string;
      };
      hotelWebsite?: {
        readonly value: string;
      };
      googleMaps?: {
        readonly gplid?: string;
        readonly name?: string;
        readonly address?: string;
        readonly lat?: string;
        readonly lng?: string;
      };
      source?: string | null;
      utm_source?: string | null;
      utm_source_secondary?: string | null;
      utm_medium?: string | null;
      utm_campaign?: string | null;
      utm_content?: string | null;
      redirectToSafeLink?: boolean;
      /** Direct booking: no surface shows the rate, so the price fields above are telemetry-only. */
      isDirectBooking?: boolean;
      onTelemetrySend?: () => void;

      // For Joy room blocks
      roomBlockUrl?: URL;
      roomBlockSuccess?: boolean;

      // Active layout when the CTA was clicked (e.g. 'default', 'stylish_hover', 'typographic').
      // Forwarded by `wrapPartnerHotelRedirect` / `PartnerHotelRedirect` so redirect-time analytics
      // can be sliced by layout — parity with `accommodationsLinkInteracted`.
      accommodationLayout?: AccommodationsSectionLayoutType;
    }> {}
